import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Table, Spinner, Button, Form, InputGroup, Modal,
    Row, Col, Badge, Alert as BsAlert, Card
} from "react-bootstrap";
import SafetyAziendeService from "../../services/safety/SafetyAziendeService";
import AgenziaSelector, { useSafetyAgenziaContext } from "../../components/safety/AgenziaSelector/AgenziaSelector";
import AziendaScheda from "../../components/safety/AziendaScheda/AziendaScheda";
import SafetySettoriService from "../../services/safety/SafetySettoriService";

const CLASSI_RISCHIO = [
    { value: "",      label: "Tutte" },
    { value: "BASSO", label: "Basso" },
    { value: "MEDIO", label: "Medio" },
    { value: "ALTO",  label: "Alto" },
];

const EMPTY_AZIENDA = {
    id_azienda: null,
    ragione_sociale: "",
    partita_iva: "",
    codice_fiscale: "",
    codice_ateco: "",
    settore: "",
    settore_codice: "GENERICO",
    settori_codici: "GENERICO",
    classe_rischio: "",
    numero_dipendenti: 0,
    indirizzo_sede_legale: "",
    cap_sede_legale: "",
    citta_sede_legale: "",
    provincia_sede_legale: "",
    nazione_sede_legale: "Italia",
    telefono: "",
    email: "",
    pec: "",
    referente_nome: "",
    referente_email: "",
    referente_telefono: "",
    datore_lavoro_nome: "",
    rspp_nome: "",
    rspp_esterno: 0,
    medico_competente_nome: "",
    medico_competente_email: "",
    rls_nome: "",
    addetto_antincendio: "",
    addetto_primo_soccorso: "",
    note: "",
    attiva: 1,
};

function classeRischioBadge(c) {
    if (!c) return <span className="text-muted">—</span>;
    const tone = c === "ALTO" ? "danger" : c === "MEDIO" ? "warning" : "success";
    return <Badge bg={tone}>{c}</Badge>;
}

export default function SafetyAziendePage() {
    const { idAgenzia, setIdAgenzia, isManager } = useSafetyAgenziaContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading]     = useState(true);
    const [aziende, setAziende]     = useState([]);
    const [q, setQ]                 = useState("");
    const [filtroRischio, setFR]    = useState("");
    const [errore, setErrore]       = useState(null);

    const [settori, setSettori]     = useState([]);
    const [modalShow, setModalShow] = useState(false);
    const [modalTab, setModalTab]   = useState("dati");
    const [editing, setEditing]     = useState(EMPTY_AZIENDA);
    const [saving, setSaving]       = useState(false);
    const [formError, setFormError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErrore(null);
        try {
            const filtri = {};
            if (q) filtri.q = q;
            if (filtroRischio) filtri.classe_rischio = filtroRischio;
            if (idAgenzia) filtri.id_agenzia = Number(idAgenzia);
            const resp = await SafetyAziendeService.find({ filtri, pagina: 0, limite: 200 });
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setAziende(Array.isArray(data) ? data : []);
        } catch (err) {
            setErrore(err?.message || "Errore nel caricamento delle aziende");
        } finally {
            setLoading(false);
        }
    }, [q, filtroRischio, idAgenzia]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const resp = await SafetySettoriService.list();
                const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
                if (mounted) setSettori(Array.isArray(data) ? data : []);
            } catch {
                if (mounted) setSettori([]);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Vista AGENZIA (mono-azienda): la scheda è gestita INLINE in pagina, quindi
    // sincronizziamo `editing` con la propria azienda (o EMPTY se da creare).
    const primaryAziendaId = aziende.length > 0 ? aziende[0].id_azienda : null;
    useEffect(() => {
        if (isManager) return;
        const a = aziende.length > 0 ? aziende[0] : null;
        setEditing(a ? { ...EMPTY_AZIENDA, ...a } : { ...EMPTY_AZIENDA });
        setFormError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isManager, primaryAziendaId]);

    // Deep-link dal "Centro Adempimenti": ?tab=documenti|attrezzature|dati apre
    // direttamente la scheda azienda sul tab corrispondente.
    const requestedTab = searchParams.get("tab");
    useEffect(() => {
        if (!requestedTab) return;
        const tab = ["dati", "sedi", "documenti", "nomine", "licenze", "valutazioni", "appalti", "infortuni", "riunioni", "sopralluoghi", "dpi", "particolari", "ispettiva", "dotazioni", "attrezzature"].includes(requestedTab) ? requestedTab : "dati";
        setModalTab(tab);
        const ctx = !isManager || !!idAgenzia;
        if (isManager) {
            // Per i manager apriamo la scheda (modale) della prima azienda del
            // contesto agenzia selezionato, se disponibile.
            if (ctx && aziende.length > 0) {
                setEditing({ ...EMPTY_AZIENDA, ...aziende[0] });
                setFormError(null);
                setModalShow(true);
                setSearchParams({}, { replace: true });
            }
        } else {
            // Vista agenzia: il tab inline viene applicato direttamente.
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedTab, isManager, idAgenzia, aziende]);

    const openNew = () => { setEditing({ ...EMPTY_AZIENDA }); setFormError(null); setModalTab("dati"); setModalShow(true); };
    const openEdit = (a) => { setEditing({ ...EMPTY_AZIENDA, ...a }); setFormError(null); setModalTab("dati"); setModalShow(true); };
    const closeModal = () => { if (!saving) { setModalShow(false); } };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditing(prev => ({
            ...prev,
            [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
        }));
    };

    // Comparti multipli (CSV in settori_codici). settore_codice = comparto primario.
    const selectedSettori = String(editing.settori_codici || editing.settore_codice || "GENERICO")
        .split(",").map(s => s.trim()).filter(Boolean);

    const toggleSettore = (codice) => {
        setEditing(prev => {
            const cur = String(prev.settori_codici || prev.settore_codice || "GENERICO")
                .split(",").map(s => s.trim()).filter(Boolean);
            let next = cur.includes(codice) ? cur.filter(c => c !== codice) : [...cur, codice];
            if (next.length === 0) next = ["GENERICO"];
            return { ...prev, settori_codici: next.join(","), settore_codice: next[0] };
        });
    };

    const handleSave = async () => {
        setFormError(null);
        if (!editing.ragione_sociale || editing.ragione_sociale.trim() === "") {
            setFormError("La ragione sociale è obbligatoria.");
            return;
        }
        setSaving(true);
        try {
            if (editing.id_azienda) {
                await SafetyAziendeService.update(editing);
            } else {
                // In creazione: se il manager ha selezionato un'agenzia di
                // contesto, la nuova azienda viene assegnata a quell'agenzia.
                const payload = idAgenzia ? { ...editing, id_agenzia: Number(idAgenzia) } : editing;
                await SafetyAziendeService.create(payload);
            }
            setModalShow(false);
            await load();
        } catch (err) {
            setFormError(err?.message || "Errore durante il salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (a) => {
        if (!window.confirm(`Eliminare l'azienda "${a.ragione_sociale}"? L'operazione è reversibile via DB ma non dall'interfaccia.`)) {
            return;
        }
        try {
            await SafetyAziendeService.delete(a.id_azienda);
            await load();
        } catch (err) {
            setErrore(err?.message || "Errore durante l'eliminazione.");
        }
    };

    // Modello a 2 livelli: l'agenzia gestisce la PROPRIA azienda (creata in
    // automatico all'attivazione GAS). Per un manager senza agenzia selezionata
    // ("Tutte") la creazione non ha un'agenzia di destinazione.
    const hasContext = !isManager || !!idAgenzia;
    const primaryAzienda = aziende.length > 0 ? aziende[0] : null;

    return (
        <div className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">
                        <i className="bi bi-building me-2 text-primary" />
                        La mia azienda
                    </h4>
                    <div className="text-muted small">
                        Dati dell'azienda di cui gestisci la compliance (D.Lgs. 81/2008)
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <AgenziaSelector idAgenzia={idAgenzia} onChange={setIdAgenzia} />
                    {/* I pulsanti di creazione/gestione lista sono solo per i manager
                        (vista multi-società). L'agenzia gestisce la propria azienda
                        direttamente inline, senza modale. */}
                    {!hasContext ? (
                        <span className="text-muted small fst-italic">
                            <i className="bi bi-arrow-left-circle me-1" />
                            Seleziona un'agenzia per gestirne l'azienda
                        </span>
                    ) : isManager ? (
                        primaryAzienda ? (
                            <Button variant="outline-secondary" size="sm" onClick={openNew} title="Gestione multi-società (solo amministrazione)">
                                <i className="bi bi-plus-lg me-1" /> Aggiungi ragione sociale
                            </Button>
                        ) : (
                            <Button variant="primary" onClick={openNew}>
                                <i className="bi bi-pencil-square me-1" /> Aggiungi azienda
                            </Button>
                        )
                    ) : null}
                </div>
            </div>

            {errore && <BsAlert variant="danger" onClose={() => setErrore(null)} dismissible>{errore}</BsAlert>}

            <BsAlert variant="light" className="border small text-muted">
                <i className="bi bi-info-circle me-2" />
                In questa sezione gestisci <strong>l'anagrafica della tua azienda</strong> (datore di lavoro,
                RSPP, medico competente, sedi, ecc.) e i relativi <strong>documenti</strong>. Usa i pulsanti
                <em> "Dati azienda"</em> e <em> "Documenti"</em> sulla scheda qui sotto.
                {isManager && <> L'aggiunta di altre ragioni sociali è riservata all'amministrazione.</>}
            </BsAlert>

            {isManager && (
                <Row className="g-2 align-items-end mb-3">
                    <Col xs={12} sm={6} md={5}>
                        <Form.Label className="small text-muted mb-1">Cerca</Form.Label>
                        <InputGroup>
                            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                            <Form.Control
                                placeholder="Ragione sociale, P.IVA, C.F."
                                value={q}
                                onChange={e => setQ(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") load(); }}
                            />
                        </InputGroup>
                    </Col>
                    <Col xs={6} sm={3} md={2}>
                        <Form.Label className="small text-muted mb-1">Classe rischio</Form.Label>
                        <Form.Select value={filtroRischio} onChange={e => setFR(e.target.value)}>
                            {CLASSI_RISCHIO.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs="auto">
                        <Button variant="outline-secondary" onClick={load}>
                            <i className="bi bi-arrow-clockwise me-1" /> Aggiorna
                        </Button>
                    </Col>
                </Row>
            )}

            {loading ? (
                <div className="text-center py-5"><Spinner /></div>
            ) : isManager && aziende.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <i className="bi bi-building fs-1 d-block mb-2 opacity-50" />
                    {!hasContext
                        ? <>Seleziona un'agenzia dal selettore in alto per gestirne l'azienda.</>
                        : <>Nessuna azienda presente. Premi <strong>"Aggiungi azienda"</strong> per iniziare.</>}
                </div>
            ) : isManager ? (
                <div className="table-responsive bg-white rounded shadow-sm">
                    <Table hover className="mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Ragione sociale</th>
                                <th>P. IVA</th>
                                <th>Settore</th>
                                <th>Classe rischio</th>
                                <th className="text-end">Dipendenti</th>
                                <th>Stato</th>
                                <th className="text-end">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {aziende.map(a => (
                                <tr key={a.id_azienda}>
                                    <td>
                                        <div className="fw-semibold">{a.ragione_sociale}</div>
                                        {a.citta_sede_legale && <div className="small text-muted">{a.citta_sede_legale} {a.provincia_sede_legale ? `(${a.provincia_sede_legale})` : ""}</div>}
                                    </td>
                                    <td>{a.partita_iva || <span className="text-muted">—</span>}</td>
                                    <td>{a.settore || <span className="text-muted">—</span>}</td>
                                    <td>{classeRischioBadge(a.classe_rischio)}</td>
                                    <td className="text-end">{a.numero_dipendenti || 0}</td>
                                    <td>
                                        {Number(a.attiva) === 1
                                            ? <Badge bg="success">Attiva</Badge>
                                            : <Badge bg="secondary">Disattivata</Badge>}
                                    </td>
                                    <td className="text-end">
                                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(a)} title="Apri scheda">
                                            <i className="bi bi-folder2-open" />
                                        </Button>
                                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(a)} title="Elimina">
                                            <i className="bi bi-trash" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : !hasContext ? (
                <div className="text-center text-muted py-5">
                    <i className="bi bi-building fs-1 d-block mb-2 opacity-50" />
                    Seleziona un'agenzia dal selettore in alto per gestirne l'azienda.
                </div>
            ) : (
                /* Vista AGENZIA (prodotto mono-azienda): la scheda è gestita
                   direttamente in pagina a tab, senza modale. */
                <Card className="border-0 shadow-sm">
                    <Card.Body>
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <h5 className="mb-0">
                                <i className="bi bi-building me-2 text-primary" />
                                {editing.ragione_sociale
                                    ? editing.ragione_sociale
                                    : <span className="fst-italic text-muted">Completa i dati della tua azienda</span>}
                            </h5>
                            <div className="d-flex align-items-center gap-2">
                                {editing.id_azienda && (
                                    Number(editing.attiva) === 1
                                        ? <Badge bg="success">Attiva</Badge>
                                        : <Badge bg="secondary">Disattivata</Badge>
                                )}
                                <Button variant="primary" onClick={handleSave} disabled={saving}>
                                    {saving ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-check2 me-1" />}
                                    Salva
                                </Button>
                            </div>
                        </div>

                        {formError && <BsAlert variant="danger">{formError}</BsAlert>}

                        <AziendaScheda
                            editing={editing}
                            onChange={handleChange}
                            settori={settori}
                            selectedSettori={selectedSettori}
                            onToggleSettore={toggleSettore}
                            activeTab={modalTab}
                            onTabChange={(k) => setModalTab(k || "dati")}
                        />

                        <div className="d-flex justify-content-end mt-2">
                            <Button variant="primary" onClick={handleSave} disabled={saving}>
                                {saving ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-check2 me-1" />}
                                Salva
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Modale crea/modifica */}
            <Modal show={modalShow} onHide={closeModal} size="lg" backdrop="static">
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>
                        {editing.id_azienda
                            ? <span><i className="bi bi-building me-2" />Scheda azienda{editing.ragione_sociale ? ` — ${editing.ragione_sociale}` : ""}</span>
                            : <span><i className="bi bi-building-add me-2" />Nuova ragione sociale</span>}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <BsAlert variant="danger">{formError}</BsAlert>}
                    <AziendaScheda
                        editing={editing}
                        onChange={handleChange}
                        settori={settori}
                        selectedSettori={selectedSettori}
                        onToggleSettore={toggleSettore}
                        activeTab={modalTab}
                        onTabChange={(k) => setModalTab(k || "dati")}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal} disabled={saving}>Annulla</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-check2 me-1" />}
                        Salva
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
